import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Phone,
  PhoneCall,
  PhoneOff,
  PhoneMissed,
  Clock,
  Calendar,
  User,
  ChevronRight,
  MessageCircle,
  AlertCircle,
  CheckCircle,
  XCircle,
  CalendarPlus,
  Search,
  Activity,
  ArrowUpRight,
  BarChart3,
  TrendingUp,
  Zap,
  ChevronDown
} from 'lucide-react';
import { STAGE_COLORS } from '../../lib/constants';
import { getWhatsappLink, formatDate, isWonStage } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { generateCallCalendarUrl, openInGoogleCalendar } from '../../utils/googleCalendar';
import { useGoogleCalendar } from '../../contexts/GoogleCalendarContext';
import { LogCallModal } from '../modals';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui';

// ─── Call outcome options ─────────────────────────────────────────────────────
const CALL_OUTCOMES = [
  { id: 'connected_interested',     label: 'Connected - Interested',          labelUr: 'رابطہ ہوا - دلچسپی ہے',       icon: CheckCircle, color: 'green'  },
  { id: 'connected_not_interested', label: 'Connected - Not Interested',      labelUr: 'رابطہ ہوا - دلچسپی نہیں',    icon: XCircle,     color: 'red'    },
  { id: 'connected_callback',       label: 'Connected - Callback Requested',  labelUr: 'رابطہ ہوا - دوبارہ کال کریں', icon: PhoneCall,   color: 'blue'   },
  { id: 'no_answer',                label: 'No Answer',                       labelUr: 'جواب نہیں',                   icon: PhoneMissed, color: 'amber'  },
  { id: 'busy',                     label: 'Busy / Call Later',               labelUr: 'مصروف / بعد میں کال کریں',   icon: Clock,       color: 'orange' },
  { id: 'wrong_number',             label: 'Wrong Number',                    labelUr: 'غلط نمبر',                    icon: PhoneOff,    color: 'gray'   },
];

// ─── Avatar ───────────────────────────────────────────────────────────────────
function LeadAvatar({ name, priority }) {
  const initials = (name || '?')
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

  const gradient = {
    overdue:  'from-red-500 to-rose-600',
    new:      'from-amber-400 to-orange-500',
    today:    'from-blue-500 to-indigo-600',
    upcoming: 'from-slate-400 to-slate-500',
  }[priority] || 'from-slate-400 to-slate-500';

  return (
    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 shadow-sm`}>
      <span className="text-white text-xs font-bold tracking-wide">{initials}</span>
    </div>
  );
}

// ─── Priority badge ───────────────────────────────────────────────────────────
function PriorityBadge({ priority, label }) {
  const style = {
    overdue:  'bg-red-50 text-red-600 border border-red-200',
    new:      'bg-amber-50 text-amber-600 border border-amber-200',
    today:    'bg-blue-50 text-blue-600 border border-blue-200',
    upcoming: 'bg-slate-50 text-slate-500 border border-slate-200',
  }[priority] || 'bg-slate-50 text-slate-500 border border-slate-200';

  const icon = {
    overdue:  <AlertCircle className="w-3 h-3" />,
    new:      <Zap className="w-3 h-3" />,
    today:    <Clock className="w-3 h-3" />,
    upcoming: <Calendar className="w-3 h-3" />,
  }[priority];

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${style}`}>
      {icon}
      {label}
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function CallListView({
  leads,
  currentUser,
  isLoading,
  onLogCall,
  onSelectLead,
  onSaveLead,
  venues,
  eventTypes,
  managers,
}) {
  const { t, language } = useLanguage();
  useGoogleCalendar(); // keep context subscription alive
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [searchParams] = useSearchParams();
  const initialFilter = searchParams.get('filter') || 'all';
  const [filterType, setFilterType] = useState(initialFilter);
  const [searchQuery, setSearchQuery] = useState('');
  const [managerFilter, setManagerFilter] = useState('All');
  const [displayLimit, setDisplayLimit] = useState(50);
  const navigate = useNavigate();

  // Today as YYYY-MM-DD (local time)
  const today =
    new Date().getFullYear() +
    '-' + String(new Date().getMonth() + 1).padStart(2, '0') +
    '-' + String(new Date().getDate()).padStart(2, '0');

  const [currentTime, setCurrentTime] = useState(Date.now());

  React.useEffect(() => {
    const timer = setInterval(() => setCurrentTime(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  // ── Categorise leads ────────────────────────────────────────────────────────
  const callList = useMemo(() => {
    let userLeads =
      currentUser?.role === 'Owner' || currentUser?.role === 'Admin'
        ? leads
        : leads.filter(l => l.manager === (currentUser?.username || currentUser?.name));

    if (managerFilter !== 'All') {
      userLeads = userLeads.filter(l => l.manager === managerFilter);
    }

    const isValidDateStr = (d) => d && d !== '-';
    const activeLeads = userLeads.filter(
      l =>
        (l.inCallList === true || l.stage === 'Call List' || !!l.nextCallDate || (isValidDateStr(l.siteVisitDate) && l.stage !== 'Site Visited' && !l.siteVisited)) &&
        !isWonStage(l.stage) &&
        l.stage !== 'Lost',
    );

    const overdue = [];
    const todayCalls = [];
    const upcoming = [];

    activeLeads.forEach(lead => {
      const hasNextCall = !!lead.nextCallDate;
      const hasSiteVisit = isValidDateStr(lead.siteVisitDate);
      const targetDate = hasNextCall ? lead.nextCallDate : (hasSiteVisit ? lead.siteVisitDate : null);

      if (!targetDate) {
        const createdAtTime = new Date(lead.createdAt || 0).getTime();
        const ms24h = 24 * 60 * 60 * 1000;
        if (currentTime - createdAtTime > ms24h) {
          overdue.push({ ...lead, callPriority: 'overdue', callStatus: t('overdue') + ': Not contacted within 24 hrs' });
        } else {
          todayCalls.push({ ...lead, callPriority: 'new', callStatus: t('newLeadNeverCalled') });
        }
        return;
      }

      const nextDateStr = targetDate.split('T')[0];
      let isOverdue = false;
      let isToday = false;
      
      let extractedSiteVisitTime = lead.siteVisitTime;
      if (!extractedSiteVisitTime && lead.siteVisitDate) {
        try {
          const d = new Date(lead.siteVisitDate);
          if (!isNaN(d.getTime())) {
            extractedSiteVisitTime = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
          }
        } catch (e) {}
      }
      
      const targetTime = hasNextCall ? lead.nextCallTime : extractedSiteVisitTime;
      const isSiteVisitFallback = !hasNextCall && hasSiteVisit;

      if (nextDateStr < today) {
        isOverdue = true;
      } else if (nextDateStr === today) {
        if (targetTime) {
          const scheduled = new Date(`${nextDateStr}T${targetTime}:00`);
          if (!isNaN(scheduled.getTime()) && currentTime - scheduled.getTime() > 24 * 60 * 60 * 1000) {
            isOverdue = true;
          } else {
            isToday = true;
          }
        } else {
          isToday = true;
        }
      }

      const statusPrefix = isSiteVisitFallback ? (language === 'ur' ? 'سائٹ وزٹ' : 'Site Visit') : '';

      if (isOverdue) {
        overdue.push({
          ...lead,
          callPriority: 'overdue',
          callStatus: isSiteVisitFallback
            ? `${t('overdue')} ${statusPrefix}: ${formatDate(nextDateStr)}${targetTime ? ` ${targetTime}` : ''}`
            : `${t('overdue')}: ${formatDate(nextDateStr)}${targetTime ? ` ${targetTime}` : ''}`,
        });
      } else if (isToday) {
        todayCalls.push({
          ...lead,
          callPriority: 'today',
          callStatus: isSiteVisitFallback
             ? `${statusPrefix} ${t('today')}${targetTime ? ` ${t('at')} ${targetTime}` : ''}`
             : (targetTime ? `${t('todayAt')} ${targetTime}` : t('today')),
        });
      } else {
        upcoming.push({
          ...lead,
          callPriority: 'upcoming',
          callStatus: isSiteVisitFallback
             ? `${statusPrefix}: ${formatDate(nextDateStr)}${targetTime ? ` ${t('at')} ${targetTime}` : ''}`
             : `${formatDate(nextDateStr)}${targetTime ? ` ${t('at')} ${targetTime}` : ''}`,
        });
      }
    });

    const getTargetDate = (l) => !!l.nextCallDate ? l.nextCallDate : (isValidDateStr(l.siteVisitDate) ? l.siteVisitDate : 0);
    const getTargetTime = (l) => {
      if (!!l.nextCallDate) return l.nextCallTime;
      if (!isValidDateStr(l.siteVisitDate)) return null;
      if (l.siteVisitTime) return l.siteVisitTime;
      try {
        const d = new Date(l.siteVisitDate);
        if (!isNaN(d.getTime())) {
          return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
        }
      } catch (e) {}
      return null;
    };

    overdue.sort((a, b) => new Date(getTargetDate(a)) - new Date(getTargetDate(b)));
    todayCalls.sort((a, b) => {
      const aTime = getTargetTime(a);
      const bTime = getTargetTime(b);
      if (!aTime && !bTime) return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      if (!aTime) return 1;
      if (!bTime) return -1;
      return aTime.localeCompare(bTime);
    });
    upcoming.sort((a, b) => {
      const d = new Date(getTargetDate(a)) - new Date(getTargetDate(b));
      if (d !== 0) return d;
      const aTime = getTargetTime(a);
      const bTime = getTargetTime(b);
      if (!aTime) return 1;
      if (!bTime) return -1;
      return aTime.localeCompare(bTime);
    });

    return { overdue, today: todayCalls, upcoming };
  }, [leads, currentUser, today, t, currentTime, managerFilter]);

  // ── Filter + search ─────────────────────────────────────────────────────────
  const handleFilterChange = type => {
    setFilterType(type);
    setDisplayLimit(50);
  };

  const filteredList = useMemo(() => {
    switch (filterType) {
      case 'overdue':  return callList.overdue;
      case 'today':    return callList.today;
      case 'upcoming': return callList.upcoming;
      default:         return [...callList.overdue, ...callList.today, ...callList.upcoming];
    }
  }, [callList, filterType]);

  const searchedList = useMemo(() => {
    if (!searchQuery.trim()) return filteredList;
    const q = searchQuery.toLowerCase();
    return filteredList.filter(
      l =>
        (l.clientName || '').toLowerCase().includes(q) ||
        (l.phone || '').includes(q) ||
        (l.stage || '').toLowerCase().includes(q) ||
        (l.manager || '').toLowerCase().includes(q),
    );
  }, [filteredList, searchQuery]);

  // ── Stats ───────────────────────────────────────────────────────────────────
  const stats = {
    overdue:  callList.overdue.length,
    today:    callList.today.length,
    upcoming: callList.upcoming.length,
    total:    callList.overdue.length + callList.today.length + callList.upcoming.length,
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const getPriorityLabel = priority => ({
    overdue:  language === 'ur' ? 'تاخیر'     : 'Overdue',
    new:      language === 'ur' ? 'نیا'        : 'New Lead',
    today:    language === 'ur' ? 'آج'         : 'Today',
    upcoming: language === 'ur' ? 'آنے والا'   : 'Upcoming',
  }[priority] || '');

  const getRowAccent = priority => ({
    overdue:  'border-l-[3px] border-l-red-400 hover:bg-red-50/40',
    new:      'border-l-[3px] border-l-amber-400 hover:bg-amber-50/40',
    today:    'border-l-[3px] border-l-blue-400 hover:bg-blue-50/40',
    upcoming: 'border-l-[3px] border-l-slate-200 hover:bg-slate-50/60',
  }[priority] || 'hover:bg-slate-50/60');

  const getTabLabel = () => ({
    overdue:  language === 'ur' ? 'تاخیر سے کالیں'  : 'Overdue Calls',
    today:    language === 'ur' ? 'آج کی کالیں'      : "Today's Calls",
    upcoming: language === 'ur' ? 'آنے والی کالیں'   : 'Upcoming Calls',
  }[filterType] || (language === 'ur' ? 'تمام کالیں' : 'All Calls'));

  // ── Stat card definitions ───────────────────────────────────────────────────
  const statCards = [
    {
      key: 'all',
      label:           language === 'ur' ? 'کل'         : 'Total Calls',
      value:           stats.total,
      Icon:            BarChart3,
      activeGradient:  'from-slate-700 to-slate-900',
      inactiveGradient:'from-white to-slate-50',
      inactiveBorder:  'border-slate-200',
      inactiveText:    'text-slate-700',
      inactiveIcon:    'text-slate-500',
      inactiveIconBg:  'bg-slate-100',
      pulse:           false,
    },
    {
      key: 'overdue',
      label:           language === 'ur' ? 'تاخیر'      : 'Overdue',
      value:           stats.overdue,
      Icon:            AlertCircle,
      activeGradient:  'from-red-500 to-rose-600',
      inactiveGradient:'from-white to-red-50',
      inactiveBorder:  'border-red-100',
      inactiveText:    'text-red-700',
      inactiveIcon:    'text-red-500',
      inactiveIconBg:  'bg-red-100',
      pulse:           stats.overdue > 0,
    },
    {
      key: 'today',
      label:           language === 'ur' ? 'آج'         : "Today's Calls",
      value:           stats.today,
      Icon:            Clock,
      activeGradient:  'from-blue-500 to-indigo-600',
      inactiveGradient:'from-white to-blue-50',
      inactiveBorder:  'border-blue-100',
      inactiveText:    'text-blue-700',
      inactiveIcon:    'text-blue-500',
      inactiveIconBg:  'bg-blue-100',
      pulse:           false,
    },
    {
      key: 'upcoming',
      label:           language === 'ur' ? 'آنے والے'   : 'Upcoming',
      value:           stats.upcoming,
      Icon:            Calendar,
      activeGradient:  'from-emerald-500 to-teal-600',
      inactiveGradient:'from-white to-emerald-50',
      inactiveBorder:  'border-emerald-100',
      inactiveText:    'text-emerald-700',
      inactiveIcon:    'text-emerald-500',
      inactiveIconBg:  'bg-emerald-100',
      pulse:           false,
    },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-5 min-h-0 max-h-[calc(100dvh-7rem)]">

      {/* ── Page Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-2 flex-shrink-0">
        <div>
          <div className="flex items-center gap-3">
              {/* <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md shadow-blue-200">
                <PhoneCall className="w-4 h-4 text-white" />
              </div> */}
            <div>
              <h1 className="text-xl sm:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
                {language === 'ur' ? 'کال لسٹ' : 'Call List'}
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                {stats.overdue > 0
                  ? (language === 'ur'
                    ? `${stats.overdue} تاخیر · ${stats.today} آج · ${stats.upcoming} آنے والے`
                    : `${stats.overdue} overdue · ${stats.today} today · ${stats.upcoming} upcoming`)
                  : (language === 'ur'
                    ? `${stats.today} آج · ${stats.upcoming} آنے والے`
                    : `${stats.today} scheduled today · ${stats.upcoming} upcoming`)}
              </p>
            </div>
          </div>
        </div>

        {/* Overdue alert pill */}
        {stats.overdue > 0 && (
          <div className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 bg-red-50 border border-red-200 rounded-lg text-xs font-semibold text-red-600 flex-shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            {stats.overdue} {language === 'ur' ? 'تاخیر' : 'overdue'}
          </div>
        )}
      </div>

      {/* ── Stat Cards ───────────────────────────────────────────────────────── */}
      <div className={`grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 flex-shrink-0 ${isLoading ? 'opacity-60 pointer-events-none' : ''}`}>
        {isLoading && stats.total === 0 ? (
          [1, 2, 3, 4].map(i => (
            <div key={i} className="p-2.5 sm:p-4 rounded-xl sm:rounded-2xl bg-white border border-slate-100 shadow-sm flex items-center gap-2.5 sm:block">
              <Skeleton width={32} height={32} animation="wave" className="shrink-0 sm:mb-2" />
              <div className="flex-1">
                <Skeleton width={50} height={14} animation="wave" className="mb-1" />
                <Skeleton width={35} height={12} animation="wave" />
              </div>
            </div>
          ))
        ) : (
          statCards.map(({ key, label, value, Icon, activeGradient, inactiveGradient, inactiveBorder, inactiveText, inactiveIcon, inactiveIconBg, pulse }) => {
            const isActive = filterType === key;
            return (
              <button
                key={key}
                onClick={() => handleFilterChange(key)}
                className={`relative p-2.5 sm:p-4 rounded-xl sm:rounded-2xl text-left transition-all duration-200 overflow-hidden ${
                  isActive
                    ? `bg-gradient-to-br ${activeGradient} text-white shadow-md sm:shadow-lg`
                    : `bg-gradient-to-br ${inactiveGradient} border ${inactiveBorder} hover:shadow-md hover:-translate-y-0.5`
                }`}
              >
                {isActive && <div className="absolute inset-0 bg-white/5 rounded-xl sm:rounded-2xl" />}
                {pulse && !isActive && (
                  <span className="absolute top-1.5 right-1.5 sm:top-3 sm:right-3 flex h-1.5 w-1.5 sm:h-2 sm:w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 sm:h-2 sm:w-2 bg-red-500" />
                  </span>
                )}
                <div className="flex items-center gap-2.5 sm:block">
                  <div className="flex items-center justify-between sm:mb-3">
                    <div className={`w-8 h-8 rounded-lg sm:rounded-xl flex items-center justify-center shrink-0 ${isActive ? 'bg-white/20' : inactiveIconBg}`}>
                      <Icon className={`w-4 h-4 ${isActive ? 'text-white' : inactiveIcon}`} />
                    </div>
                    {isActive && <ArrowUpRight className="w-3.5 h-3.5 text-white/60 hidden sm:block" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-base sm:text-2xl font-bold leading-none sm:mb-1 ${isActive ? 'text-white' : inactiveText}`}>{value}</p>
                    <p className={`text-[11px] font-medium truncate mt-0.5 sm:mt-0 ${isActive ? 'text-white/80' : 'text-slate-400'}`}>{label}</p>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* ── Call List Panel ───────────────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">

        {/* Toolbar */}
        <div className="p-3 sm:px-4 sm:py-3 border-b border-slate-100 bg-slate-50/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3 flex-shrink-0">
          <div className="flex items-center justify-between sm:justify-start gap-2 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <PhoneCall className="w-3 h-3 text-white" />
              </div>
              <h2 className="font-semibold text-slate-800 text-sm truncate">{getTabLabel()}</h2>
              <span className="flex-shrink-0 px-2 py-0.5 bg-slate-200 text-slate-600 rounded-full text-[10px] font-bold">
                {searchedList.length}
              </span>
            </div>

            {/* Filter tabs */}
            <div className="flex items-center gap-0.5 sm:gap-1 bg-white border border-slate-200 rounded-lg p-0.5 flex-shrink-0">
              {[
                { key: 'all',      label: language === 'ur' ? 'سب'       : 'All'   },
                { key: 'overdue',  label: language === 'ur' ? 'تاخیر'    : 'Late'  },
                { key: 'today',    label: language === 'ur' ? 'آج'       : 'Today' },
                { key: 'upcoming', label: language === 'ur' ? 'آنے والے' : 'Soon'  },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => handleFilterChange(tab.key)}
                  className={`px-2 sm:px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all whitespace-nowrap ${
                    filterType === tab.key
                      ? 'bg-slate-800 text-white shadow-sm'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            {/* Manager Filter */}
            {(currentUser?.role === 'Owner' || currentUser?.role === 'Admin') && (
              <div className="w-full sm:w-auto">
                <Dropdown className="w-full sm:w-auto">
                  <DropdownTrigger>
                    <button className="w-full sm:min-w-[140px] bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-xs px-3 h-[34px] focus:ring-2 focus:ring-blue-500 outline-none flex justify-between items-center text-left shadow-sm transition-colors text-slate-700">
                      <span className="font-medium truncate mr-2">
                        {managerFilter === 'All' ? (language === 'ur' ? 'تمام سیلز' : 'All Sales') : managerFilter}
                      </span>
                      <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    </button>
                  </DropdownTrigger>
                  <DropdownPopover className="w-[180px] max-h-60 overflow-y-auto">
                    <DropdownMenu
                      selectedKeys={new Set([managerFilter])}
                      selectionMode="single"
                      onSelectionChange={(keys) => setManagerFilter(Array.from(keys)[0])}
                    >
                      <DropdownItem id="All">
                        {language === 'ur' ? 'تمام سیلز' : 'All Sales'}
                      </DropdownItem>
                      {managers?.map(m => (
                        <DropdownItem key={m} id={m}>
                          {m}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </DropdownPopover>
                </Dropdown>
              </div>
            )}

            {/* Search */}
            <div className="relative w-full sm:w-44">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={language === 'ur' ? 'تلاش کریں...' : 'Search leads...'}
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all h-[34px]"
              />
            </div>
          </div>
        </div>

        {/* List body */}
        <div className={`flex-1 overflow-y-auto ${isLoading ? 'opacity-60 pointer-events-none' : ''}`}>

          {/* Loading skeletons */}
          {isLoading && filteredList.length === 0 ? (
            <div>
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="px-4 py-3.5 border-b border-slate-50 flex items-center gap-3">
                  <Skeleton variant="rounded" width={40} height={40} animation="wave" className="rounded-xl flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Skeleton width={140} height={14} animation="wave" />
                    <Skeleton width={110} height={11} animation="wave" />
                    <Skeleton width={160} height={11} animation="wave" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton variant="rounded" width={32} height={32} animation="wave" className="rounded-lg" />
                    <Skeleton variant="rounded" width={32} height={32} animation="wave" className="rounded-lg" />
                    <Skeleton variant="rounded" width={72} height={32} animation="wave" className="rounded-lg" />
                  </div>
                </div>
              ))}
            </div>

          /* Empty state */
          ) : searchedList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center mb-4 shadow-inner">
                <Phone className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-sm font-semibold text-slate-600 mb-1">
                {searchQuery
                  ? (language === 'ur' ? 'کوئی نتیجہ نہیں' : 'No results found')
                  : (language === 'ur' ? 'کوئی کال شیڈول نہیں' : 'No calls scheduled')}
              </p>
              <p className="text-xs text-slate-400">
                {searchQuery
                  ? (language === 'ur' ? 'مختلف الفاظ سے تلاش کریں' : 'Try a different search term')
                  : (language === 'ur' ? 'تمام کالیں مکمل ہو گئی ہیں' : 'All caught up — great work!')}
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-3 text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  {language === 'ur' ? 'صاف کریں' : 'Clear search'}
                </button>
              )}
            </div>

          /* Lead rows */
          ) : (
            <>
              {searchedList.slice(0, displayLimit).map(lead => {
                const stageColor = STAGE_COLORS[lead.stage] || STAGE_COLORS['New'];
                const outcomeObj = lead.lastCallOutcome
                  ? CALL_OUTCOMES.find(o => o.id === lead.lastCallOutcome)
                  : null;
                const friendlyOutcome = outcomeObj
                  ? (language === 'ur' ? outcomeObj.labelUr : outcomeObj.label)
                  : lead.lastCallOutcome;
                const lastCallDateStr = lead.lastCallDate
                  ? new Date(lead.lastCallDate).toLocaleString('en-PK', {
                      day: '2-digit', month: 'short',
                      hour: '2-digit', minute: '2-digit', hour12: true,
                    })
                  : null;
                const waLink = getWhatsappLink(lead.phone);

                return (
                  <div
                    key={lead.id}
                    className={`group px-3.5 sm:px-4 py-3.5 border-b border-slate-100 transition-all duration-150 ${getRowAccent(lead.callPriority)}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      {/* Avatar & Lead info */}
                      <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
                        {/* Avatar */}
                        <LeadAvatar name={lead.clientName} priority={lead.callPriority} />

                        {/* Lead info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                            <span className="font-bold text-slate-900 text-sm sm:text-base truncate max-w-[200px] sm:max-w-none">
                              {lead.clientName}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${stageColor.bg} ${stageColor.text}`}>
                              {lead.stage}
                            </span>
                            <PriorityBadge priority={lead.callPriority} label={getPriorityLabel(lead.callPriority)} />
                          </div>

                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            <Phone className="w-3 h-3 text-slate-400 flex-shrink-0" />
                            <span className="text-xs text-slate-600 font-mono font-medium">{lead.phone}</span>
                            {lead.manager && (
                              <>
                                <span className="text-slate-300">·</span>
                                <User className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                <span className="text-xs text-slate-500 truncate max-w-[120px]">{lead.manager}</span>
                              </>
                            )}
                          </div>

                          <p className={`text-[11px] mt-1 font-medium ${
                            lead.callPriority === 'overdue' ? 'text-red-500' :
                            lead.callPriority === 'new'     ? 'text-amber-500' :
                            lead.callPriority === 'today'   ? 'text-blue-500' :
                            'text-slate-400'
                          }`}>
                            {lead.callPriority === 'new' ? t('newLeadMakeFirstCall') : lead.callStatus}
                          </p>

                          {friendlyOutcome && (
                            <p className="text-[11px] text-slate-400 mt-0.5">
                              {t('last')}: <span className="font-medium text-slate-500">{friendlyOutcome}</span>
                              {lastCallDateStr && <span className="text-slate-300"> · {lastCallDateStr}</span>}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Action buttons (Responsive Action Bar on Mobile) */}
                      <div className="flex items-center justify-between sm:justify-end gap-1.5 sm:gap-1.5 pt-2 sm:pt-0 border-t border-slate-100 sm:border-t-0 sm:border-none flex-shrink-0 w-full sm:w-auto">
                        {/* Call CTA - Prominent CTA on left/flex on mobile */}
                        <button
                          onClick={() => {
                            setSelectedLead(lead);
                            setShowLogModal(true);
                          }}
                          className={`flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-3.5 py-2 sm:py-1.5 rounded-lg text-xs font-bold transition-all hover:scale-105 active:scale-95 shadow-sm ${
                            lead.callPriority === 'overdue'
                              ? 'bg-gradient-to-r from-red-500 to-rose-600 text-white shadow-red-200'
                              : lead.callPriority === 'today'
                              ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-blue-200'
                              : 'bg-gradient-to-r from-slate-700 to-slate-800 text-white shadow-slate-200'
                          }`}
                        >
                          <Phone className="w-3.5 h-3.5" />
                          <span>{t('call')}</span>
                        </button>

                        {/* Add to calendar */}
                        {lead.nextCallDate && (
                          <button
                            onClick={() => openInGoogleCalendar(generateCallCalendarUrl(lead))}
                            title={t('addToCalendar')}
                            className="w-9 sm:w-8 h-9 sm:h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 hover:bg-indigo-100 hover:text-indigo-600 transition-all hover:scale-105 active:scale-95 shrink-0"
                          >
                            <CalendarPlus className="w-4 sm:w-3.5 h-4 sm:h-3.5" />
                          </button>
                        )}

                        {/* WhatsApp */}
                        {waLink && (
                          <a
                            href={waLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="WhatsApp"
                            className="w-9 sm:w-8 h-9 sm:h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 hover:bg-green-100 hover:text-green-600 transition-all hover:scale-105 active:scale-95 shrink-0"
                          >
                            <MessageCircle className="w-4 sm:w-3.5 h-4 sm:h-3.5" />
                          </a>
                        )}

                        {/* Conversation logs */}
                        <button
                          onClick={() => navigate(`/conversation-logs?chatId=${lead.id}`)}
                          title={t('conversations') || 'Conversation Logs'}
                          className="w-9 sm:w-8 h-9 sm:h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 hover:bg-blue-100 hover:text-blue-600 transition-all hover:scale-105 active:scale-95 shrink-0"
                        >
                          <Activity className="w-4 sm:w-3.5 h-4 sm:h-3.5" />
                        </button>

                        {/* View details */}
                        <button
                          onClick={() => onSelectLead(lead)}
                          title={t('viewDetails')}
                          className="w-9 sm:w-8 h-9 sm:h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all hover:scale-105 active:scale-95 shrink-0"
                        >
                          <ChevronRight className="w-4 sm:w-4 h-4 sm:h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Load more */}
              {searchedList.length > displayLimit && (
                <div className="p-4 flex justify-center border-t border-slate-50">
                  <button
                    onClick={() => setDisplayLimit(prev => prev + 50)}
                    className="flex items-center gap-2 px-5 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm hover:shadow-md"
                  >
                    <TrendingUp className="w-3.5 h-3.5" />
                    {language === 'ur' ? 'مزید لوڈ کریں' : 'Load more'}
                    <span className="px-1.5 py-0.5 bg-slate-100 rounded-md text-slate-500">
                      {searchedList.length - displayLimit}
                    </span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Log Call Modal ────────────────────────────────────────────────────── */}
      {showLogModal && selectedLead && (
        <LogCallModal
          lead={selectedLead}
          currentUser={currentUser}
          onClose={() => {
            setShowLogModal(false);
            setSelectedLead(null);
          }}
          onSave={onLogCall}
          onSaveLead={onSaveLead}
          venues={venues}
          eventTypes={eventTypes}
          managers={managers}
        />
      )}
    </div>
  );
}
